import React from 'react';
import {
  Box,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyOutlined from '@mui/icons-material/ContentCopyOutlined';
import DeleteOutlineOutlined from '@mui/icons-material/DeleteOutlineOutlined';
import DragIndicatorOutlined from '@mui/icons-material/DragIndicatorOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import { useFeatureTheme } from '../../../../../theme/hooks/useFeatureTheme';
import { FORM_STUDIO_COPY } from '../../constants/formStudioConfig';
import { getUiFieldTypeLabel } from '../../utils/fieldTypeAdapter';
import {
  isFieldDeletable,
  isStatusField,
  isSystemBuilderField,
} from '../../utils/formStudioFormUtils';
import { shouldShowFieldOptionsPreview } from '../../utils/fieldOptionsUtils';
import FieldOptionsPreview from './FieldOptionsPreview';
import { typography } from '../../../../../theme/tokens';

function StatusDetails({
  field,
  showOptions,
  copy,
  semantic,
  typographyStyles,
}) {
  if (!showOptions) {
    return (
      <Typography
        sx={{
          ...typographyStyles.caption,
          color: semantic.text.secondary,
          mt: 1,
          ml: { xs: 0, sm: 5 },
        }}
      >
        {copy.noStatusOptions}
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1.25, ml: { xs: 0, sm: 5 } }}>
      {showOptions ? (
        <FieldOptionsPreview
          keyOptions={field.key_options}
          optionColors={field.option_colors}
          maxVisible={8}
          variant="embedded"
          emptyLabel={copy.noStatusOptions}
        />
      ) : null}
    </Box>
  );
}

export default function FormFieldCard({
  field,
  index,
  fieldErrors,
  draggedIndex,
  dragEnabled = true,
  onEdit,
  onDuplicate,
  onDelete,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
}) {
  const { semantic, radiusPx, typographyStyles, shadows } = useFeatureTheme();
  const copy = FORM_STUDIO_COPY.statusField;
  const isSystemField = isSystemBuilderField(field);
  const isStatusCard = isStatusField(field);
  const showOptions = shouldShowFieldOptionsPreview(field);
  const isDragging = draggedIndex === index;
  const errorCount = fieldErrors ? Object.keys(fieldErrors).length : 0;
  const typeLabel = getUiFieldTypeLabel(field);

  const cardBorder = (() => {
    if (isDragging) return `2px dashed ${semantic.interactive.primary}`;
    if (errorCount) return `2px solid ${semantic.states.danger}`;
    return `2px solid ${semantic.borders.border}`;
  })();

  return (
    <Box
      draggable={dragEnabled}
      onDragStart={(event) => dragEnabled && onDragStart?.(event, index)}
      onDragOver={(event) => dragEnabled && onDragOver?.(event)}
      onDrop={() => dragEnabled && onDrop?.(index)}
      onDragEnd={() => onDragEnd?.()}
      sx={{
        p: 2,
        borderRadius: radiusPx.lg,
        border: cardBorder,
        borderLeft: isStatusCard
          ? `3px solid ${semantic.interactive.primary}`
          : undefined,
        bgcolor: isDragging
          ? semantic.interactive.primarySoft
          : semantic.surfaces.surface,
        boxShadow: shadows.sm,
        cursor: dragEnabled ? 'grab' : 'default',
        transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
          boxShadow: shadows.md,
        },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ minWidth: 0, flex: 1 }}>
          <Tooltip title={dragEnabled ? 'Drag to reorder' : 'Clear filters to reorder'}>
            <span>
              <IconButton
                size="small"
                disabled={!dragEnabled}
                sx={{
                  cursor: dragEnabled ? 'grab' : 'default',
                  color: isDragging ? semantic.interactive.primary : semantic.text.secondary,
                  mt: 0.25,
                }}
              >
                <DragIndicatorOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap"  sx={{
                flexWrap: {
                  xs: 'nowrap',
                  sm: 'wrap',
                },
                overflowX: {
                  xs: 'scroll',
                  sm: 'visible',
                },
                paddingBottom: {
                  xs: '10px',
                  sm: 0,
                },
                '&::-webkit-scrollbar': {
                  height: {
                    xs: '4px',
                    sm: 0,
                  },
                },
              }}>
              <Typography sx={{ ...typographyStyles.bodyText, fontWeight: 600 , minWidth:{xs:'max-content',sm:'auto'}}}>
                {field.alias_name || field.key_name || 'Untitled field'}
              </Typography>
              <Chip
                size="small"
                label={typeLabel}
                color={isStatusCard ? 'primary' : 'default'}
                variant="outlined"
              />
              {isSystemField ? (
                <Chip size="small" icon={<LockOutlined />} label="System" variant="outlined" sx={{
                  '& .MuiSvgIcon-root': {
                    fontSize:typography.fontSizeSm , marginLeft:0.8
                  },
                }} />
              ) : null}
              {field.is_primary ? <Chip size="small" label="Primary" color="primary" /> : null}
              {errorCount ? (
                <Chip
                  size="small"
                  label={`${errorCount} error${errorCount > 1 ? 's' : ''}`}
                  color="error"
                />
              ) : null}
            </Stack>

            <Typography sx={{ ...typographyStyles.caption, color: semantic.text.secondary, mt: 0.5 ,textOverflow: 'ellipsis', overflow: 'hidden',whiteSpace: 'nowrap', maxWidth: { xs: '180px', sm: 'none',} }}>
              {field.key_name === 'lead_id' ? 'record_id' : field.key_name}
            </Typography>

            {!isStatusCard && showOptions ? (
              <FieldOptionsPreview
                keyOptions={field.key_options}
                optionColors={field.option_colors}
                maxVisible={5}
                variant="compact"
              />
            ) : null}
          </Box>
        </Stack>

        <Stack direction="row" spacing={0.5} flexShrink={0}>
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => onEdit?.()}>
              <EditOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          {isFieldDeletable(field) ? (
            <>
              <Tooltip title="Duplicate">
                <IconButton size="small" onClick={() => onDuplicate?.(index)}>
                  <ContentCopyOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton size="small" onClick={() => onDelete?.()}>
                  <DeleteOutlineOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          ) : null}
        </Stack>
      </Stack>
      {isStatusCard ? (
        <StatusDetails
          field={field}
          showOptions={showOptions}
          copy={copy}
          semantic={semantic}
          typographyStyles={typographyStyles}
        />
      ) : null}
    </Box>
  );
}
